import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cropper from "react-easy-crop";
import { useAppData } from "../context/AppDataContext";
import { useModal } from "../context/ModalContext";
import { useToast } from "../context/ToastContext";
import { ApiError } from "../api/httpClient";
import { instagramApi } from "../api/instagramApi";
import { contentApi } from "../api/contentApi";
import { fileSize, formatDate, formatTime, zonedParts } from "../utils/format";
import { getCroppedFile } from "../utils/cropImage";
import DateInput from "../components/DateInput";

const ASPECTS = [
  { key: "original", label: "Original", value: null },
  { key: "square", label: "Square 1:1", value: 1 },
  { key: "portrait", label: "Portrait 4:5", value: 4 / 5 },
  { key: "landscape", label: "Landscape 1.91:1", value: 1.91 },
];

let nextItemId = 1;

export default function CreateContent({ type }) {
  const isReel = type === "REEL";
  const accept = isReel ? "video/mp4,video/quicktime" : "image/jpeg,image/png,image/jpg";
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.editId || null;
  const [editLoading, setEditLoading] = useState(!!editId);
  const [editLoadError, setEditLoadError] = useState(null);
  const { accounts, settings, createPost, createReel } = useAppData();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [step, setStep] = useState(1);

  // items: [{ id, file, previewUrl, isVideo, duration, cropApplied, aspectLabel }]
  // Images only: 1 item = single post, 2+ items = carousel (server auto-promotes).
  // Reels: always exactly 0 or 1 video item, never cropped here.
  const [items, setItems] = useState([]);

  const today = new Date().toISOString().slice(0, 10);
  const [account, setAccount] = useState("");
  const [caption, setCaption] = useState("");
  const [collaborators, setCollaborators] = useState([]);
  const [collabDraft, setCollabDraft] = useState("");
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  const [tagPeople, setTagPeople] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null); // { id, name, city, state, country }
  const [audioQuery, setAudioQuery] = useState("");
  const [audioResults, setAudioResults] = useState([]);
  const [audioSearching, setAudioSearching] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null); // { id, title, artist }
  const [publishNow, setPublishNow] = useState(false);
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("09:00");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const isCarousel = !isReel && items.length >= 2;

  // Prefill account/schedule defaults once accounts + settings have loaded.
  useEffect(() => {
    if (accounts.length && !account) setAccount(String(accounts[0].id));
  }, [accounts, account]);

  useEffect(() => {
    if (settings) {
      const defaultTime = (settings.default_publish_time || "09:00").slice(0, 5);
      setTime(defaultTime);
      setTimezone(settings.default_timezone || "Asia/Kolkata");
      const nowLocal = new Date().toTimeString().slice(0, 5);
      if (defaultTime <= nowLocal) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setDate(tomorrow.toISOString().slice(0, 10));
      }
    }
  }, [settings]);

  // reset form state when switching between Create Post / Create Reel
  useEffect(() => {
    setItems([]); setStep(1);
    setCaption(""); setCollaborators([]); setCollabDraft("");
    setIsAiGenerated(false); setTagPeople([]); setTagDraft("");
    setLocationQuery(""); setLocationResults([]); setLocationError(null); setSelectedLocation(null);
    setAudioQuery(""); setAudioResults([]); setAudioError(null); setSelectedAudio(null);
    setPublishNow(false); setDate(today); setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    return () => { items.forEach((it) => it.previewUrl?.startsWith("blob:") && URL.revokeObjectURL(it.previewUrl)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Edit mode: load an existing SCHEDULED/FAILED item and prefill the
  // composer. Media itself is shown read-only (previewUrl points at the
  // already-uploaded server file, not a local blob) -- there's no original
  // File object to re-crop or re-upload, and PATCH doesn't accept media.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const item = await contentApi.get(editId);
        if (cancelled) return;
        if (!["SCHEDULED", "FAILED"].includes(item.status)) {
          setEditLoadError(`This content is ${item.status.toLowerCase()} and can no longer be edited.`);
          return;
        }
        const mediaList = item.carousel_media?.length ? item.carousel_media : [item.media];
        setItems(mediaList.map((m) => ({
          id: nextItemId++, file: null, previewUrl: m.url, isVideo: m.kind === "VIDEO", locked: true,
        })));
        setAccount(String(item.instagram_account.id));
        setCaption(item.caption || "");
        setCollaborators(item.collaborators || []);
        setIsAiGenerated(!!item.is_ai_generated);
        setTagPeople((item.user_tags || []).map((t) => t.username));
        if (item.location_page_id) {
          const [name, rest] = (item.location_name || "").split(" — ");
          setSelectedLocation({ id: item.location_page_id, name: name || item.location_page_id, city: rest || "", state: "", country: "" });
        }
        if (item.audio_id) {
          const [title, artist] = (item.audio_name || "").split(" — ");
          setSelectedAudio({ id: item.audio_id, title: title || item.audio_id, artist: artist || "" });
        }
        const when = zonedParts(item.scheduled_at, item.timezone);
        setDate(when.date);
        setTime(when.time);
        setTimezone(item.timezone || "Asia/Kolkata");
        setPublishNow(false);
        setStep(2); // media is locked, so land straight on the editable caption step
      } catch (err) {
        if (!cancelled) setEditLoadError(err instanceof ApiError ? err.message : "Could not load this content for editing.");
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    if (isReel) {
      items.forEach((it) => URL.revokeObjectURL(it.previewUrl));
      const f = incoming[0];
      setItems([{ id: nextItemId++, file: f, previewUrl: URL.createObjectURL(f), isVideo: true, duration: null }]);
      return;
    }
    const room = 10 - items.length;
    const toAdd = incoming.slice(0, Math.max(room, 0));
    if (incoming.length > toAdd.length) showToast("A carousel can have at most 10 images -- extra files were skipped.");
    setItems((prev) => [
      ...prev,
      ...toAdd.map((f) => ({ id: nextItemId++, file: f, previewUrl: URL.createObjectURL(f), isVideo: false })),
    ]);
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const target = prev.find((it) => it.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((it) => it.id !== id);
    });
  };

  const moveItem = (fromId, toId) => {
    setItems((prev) => {
      const fromIdx = prev.findIndex((it) => it.id === fromId);
      const toIdx = prev.findIndex((it) => it.id === toId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const onVideoLoaded = (id) => (e) => {
    const dur = e.target.duration;
    const mm = Math.floor(dur / 60);
    const ss = Math.floor(dur % 60).toString().padStart(2, "0");
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, duration: `${mm}:${ss}` } : it)));
  };

  // ---- Crop dialog ----
  const [cropTarget, setCropTarget] = useState(null); // item being cropped
  const [cropAspectKey, setCropAspectKey] = useState("original");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelCrop, setPixelCrop] = useState(null);

  const onCropComplete = useCallback((_area, areaPixels) => setPixelCrop(areaPixels), []);

  const openCrop = (item) => {
    setCropTarget(item);
    setCropAspectKey("original");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setPixelCrop(null);
  };

  const applyCrop = async () => {
    if (!cropTarget || !pixelCrop) { setCropTarget(null); return; }
    const aspect = ASPECTS.find((a) => a.key === cropAspectKey);
    const cropped = await getCroppedFile(cropTarget.previewUrl, pixelCrop, cropTarget.file.name, cropTarget.file.type);
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== cropTarget.id) return it;
        URL.revokeObjectURL(it.previewUrl);
        return { ...it, file: cropped, previewUrl: URL.createObjectURL(cropped), cropApplied: aspect.key !== "original", aspectLabel: aspect.label };
      })
    );
    setCropTarget(null);
  };

  // ---- Caption hashtag/mention hints ----
  const hashtags = (caption.match(/#[A-Za-z0-9_]+/g) || []);
  const mentions = (caption.match(/@[A-Za-z0-9_.]+/g) || []);
  const charCount = caption.length;

  // ---- Collaborators / Tag people chip inputs ----
  const addCollaborator = () => {
    const name = collabDraft.replace(/^@+/, "").trim();
    if (!name) return;
    if (collaborators.length >= 3) { showToast("Up to 3 collaborators only."); return; }
    if (!/^[A-Za-z0-9._]{1,30}$/.test(name)) { showToast("That doesn't look like a valid Instagram username."); return; }
    if (collaborators.includes(name)) { setCollabDraft(""); return; }
    setCollaborators((prev) => [...prev, name]);
    setCollabDraft("");
  };
  const addTagPerson = () => {
    const name = tagDraft.replace(/^@+/, "").trim();
    if (!name) return;
    if (!/^[A-Za-z0-9._]{1,30}$/.test(name)) { showToast("That doesn't look like a valid Instagram username."); return; }
    if (tagPeople.includes(name)) { setTagDraft(""); return; }
    setTagPeople((prev) => [...prev, name]);
    setTagDraft("");
  };

  // ---- Reels location search (debounced, real Meta Pages Search results only) ----
  useEffect(() => {
    if (!isReel || selectedLocation || !locationQuery.trim() || !account) {
      setLocationResults([]);
      return;
    }
    let cancelled = false;
    setLocationSearching(true);
    setLocationError(null);
    const timer = setTimeout(async () => {
      try {
        const results = await instagramApi.searchLocations(account, locationQuery.trim());
        if (!cancelled) setLocationResults(results);
      } catch (err) {
        if (!cancelled) {
          setLocationResults([]);
          setLocationError(err instanceof ApiError ? err.message : "Location search failed. Please try again.");
        }
      } finally {
        if (!cancelled) setLocationSearching(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [locationQuery, isReel, account, selectedLocation]);

  const locationLabel = (loc) => [loc.city, loc.state, loc.country].filter(Boolean).join(", ");

  // ---- Reels audio/music search (debounced, real Instagram Audio API results only) ----
  useEffect(() => {
    if (!isReel || selectedAudio || !account) {
      setAudioResults([]);
      return;
    }
    let cancelled = false;
    setAudioSearching(true);
    setAudioError(null);
    const timer = setTimeout(async () => {
      try {
        const results = await instagramApi.searchAudio(account, "music", audioQuery.trim());
        if (!cancelled) setAudioResults(results);
      } catch (err) {
        if (!cancelled) {
          setAudioResults([]);
          setAudioError(err instanceof ApiError ? err.message : "Audio search failed. Please try again.");
        }
      } finally {
        if (!cancelled) setAudioSearching(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [audioQuery, isReel, account, selectedAudio]);

  const stepValid = (n) => {
    if (n === 1) return items.length > 0;
    return true;
  };

  const goNext = () => {
    if (!stepValid(step)) {
      showToast(`Please upload ${isReel ? "a video" : "at least one image"} first.`);
      return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    setSubmitError(null);
    if (!items.length) { showToast(`Please upload ${isReel ? "a video" : "an image"} first.`); return; }
    if (!account) { showToast("Please select an Instagram account."); return; }
    if (!publishNow && (!date || !time)) { showToast("Please select a date and time."); return; }

    const locationPayload = isReel && selectedLocation
      ? { location_page_id: selectedLocation.id, location_name: `${selectedLocation.name}${locationLabel(selectedLocation) ? " — " + locationLabel(selectedLocation) : ""}` }
      : editId ? { location_page_id: "" } : {};
    const audioPayload = isReel && selectedAudio
      ? { audio_id: selectedAudio.id, audio_name: `${selectedAudio.title}${selectedAudio.artist ? " — " + selectedAudio.artist : ""}` }
      : editId ? { audio_id: "" } : {};

    setSubmitting(true);
    try {
      if (editId) {
        // Media/account/content_type can't change here -- only the fields
        // PATCH actually accepts (see routes/content.js). A FAILED item that
        // gets a future scheduled_at is moved back to SCHEDULED server-side.
        const record = await contentApi.update(editId, {
          caption,
          scheduled_at: `${date}T${time}`,
          timezone,
          collaborators,
          is_ai_generated: isAiGenerated,
          user_tags: !isCarousel ? tagPeople : [],
          ...locationPayload,
          ...audioPayload,
        });
        showToast("Content updated.");
        navigate(record.status === "SCHEDULED" ? "/instagram-scheduler/app/scheduled" : "/instagram-scheduler/app/failed");
        return;
      }
      const payload = {
        instagram_account: account,
        caption,
        media: items.length > 1 ? items.map((it) => it.file) : items[0].file,
        scheduled_at: publishNow ? `${today}T${new Date().toTimeString().slice(0, 5)}` : `${date}T${time}`,
        timezone,
        publish_now: publishNow,
        collaborators: collaborators.length ? collaborators : undefined,
        is_ai_generated: isAiGenerated,
        user_tags: !isCarousel && tagPeople.length ? tagPeople : undefined,
        ...locationPayload,
        ...audioPayload,
      };
      const record = isReel ? await createReel(payload) : await createPost(payload);
      showConfirmModal(record, publishNow);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Failed to save content. Please try again.";
      setSubmitError(message);
      showToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  const showConfirmModal = (record, wasPublishNow) => {
    openModal(
      <>
        <button className="modal-close" onClick={() => { closeModal(); navigate("/instagram-scheduler/app/dashboard"); }}>
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <div className="modal-icon"><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg></div>
        <h3>{record.content_type} {wasPublishNow ? "Submitted" : "Scheduled"}</h3>
        <p>
          {wasPublishNow
            ? `Your ${record.content_type.toLowerCase()} has been queued for immediate publishing. Check Published/Failed shortly for the result.`
            : `Your Instagram ${record.content_type.toLowerCase()} has been scheduled successfully.`}
        </p>
        <div className="modal-detail-row"><span className="label">Account</span><span className="value">@{record.instagram_account.username}</span></div>
        <div className="modal-detail-row"><span className="label">Date</span><span className="value">{formatDate(zonedParts(record.scheduled_at, record.timezone).date)}</span></div>
        {!wasPublishNow && <div className="modal-detail-row"><span className="label">Time</span><span className="value">{formatTime(zonedParts(record.scheduled_at, record.timezone).time)}</span></div>}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => { closeModal(); navigate(wasPublishNow ? "/instagram-scheduler/app/published" : "/instagram-scheduler/app/scheduled"); }}>
            {wasPublishNow ? "View Published" : "View Schedule"}
          </button>
          <button className="btn btn-primary" onClick={() => { closeModal(); navigate("/instagram-scheduler/app/dashboard"); }}>Done</button>
        </div>
      </>
    );
    showToast(wasPublishNow ? "Content submitted for publishing." : "Content scheduled successfully.");
  };

  const selectedAccount = accounts.find((a) => String(a.id) === String(account));

  const STEP_LABELS = ["Media", "Caption", "Settings", "Review"];

  if (editId && editLoading) {
    return (
      <div className="view-header">
        <div><h2>Loading...</h2></div>
      </div>
    );
  }
  if (editId && editLoadError) {
    return (
      <>
        <div className="view-header"><div><h2>Edit {isReel ? "Reel" : "Post"}</h2></div></div>
        <div className="empty-state"><h4>Can't edit this content</h4><p>{editLoadError}</p></div>
      </>
    );
  }

  return (
    <>
      <div className="view-header">
        <div>
          <h2>{editId ? "Edit " : "Create "}{isReel ? "Reel" : "Post"}</h2>
          <div className="view-subtitle">
            {editId
              ? "Media can't be changed here -- delete and recreate if you need a different image/video."
              : isReel ? "Upload a video and schedule it to your Instagram Reels." : "Upload one or more images and schedule them to your Instagram feed."}
          </div>
        </div>
      </div>

      {accounts.length === 0 && (
        <div className="empty-state" style={{ marginBottom: 32 }}>
          <h4>No Instagram account connected</h4>
          <p>Connect an Instagram Professional account before creating content.</p>
          <button className="btn btn-primary" onClick={() => navigate("/instagram-scheduler/app/accounts")}>Go to Instagram Accounts</button>
        </div>
      )}

      <div className="composer-steps">
        {STEP_LABELS.map((label, i) => (
          <Fragment key={label}>
            <div className={`composer-step ${step === i + 1 ? "active" : step > i + 1 ? "done" : ""}`}>
              <span className="dot">{step > i + 1 ? "✓" : i + 1}</span>
              <span>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && <div className="composer-step-line" />}
          </Fragment>
        ))}
      </div>

      {/* ---- Step 1: Media (locked, read-only, in edit mode) ---- */}
      {step === 1 && editId && (
        <div className="create-layout">
          <div className="create-media-col">
            <div className="media-preview-wrap">
              {items[0]?.isVideo ? (
                <video src={items[0].previewUrl} className="media-preview" controls />
              ) : (
                <img src={items[0]?.previewUrl} className="media-preview" alt="Preview" />
              )}
            </div>
            {items.length > 1 && (
              <div className="thumb-strip">
                {items.map((it, idx) => (
                  <div key={it.id} className="thumb-item">
                    <span className="thumb-pos">{idx + 1}</span>
                    <img src={it.previewUrl} alt={`Item ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="field-disabled-note">Media can't be changed while editing -- delete this content and create a new one if you need different image(s)/video.</div>
          </div>
        </div>
      )}

      {/* ---- Step 1: Media ---- */}
      {step === 1 && !editId && (
        <div className="create-layout">
          <div className="create-media-col">
            {items.length === 0 ? (
              <div
                className={`upload-box ${dragOver ? "dragover" : ""}`}
                onClick={() => fileInputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              >
                <svg viewBox="0 0 24 24"><path d="M12 16V4M12 4l-5 5M12 4l5 5" /><path d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3" /></svg>
                <h4>Upload {isReel ? "Reel Video" : "Image(s)"}</h4>
                <p>Drag &amp; drop your {isReel ? "video" : "image(s)"} here</p>
                <div className="or">or</div>
                <span className="browse-link">Browse from computer</span>
                <div className="accept-types">{isReel ? "MP4 · MOV" : "JPG · JPEG · PNG · up to 10 for a carousel"}</div>
              </div>
            ) : isReel ? (
              <div className="media-preview-wrap">
                <video src={items[0].previewUrl} className="media-preview" controls onLoadedMetadata={onVideoLoaded(items[0].id)} />
                <div className="media-meta-bar">
                  <div className="media-meta-info">
                    <div className="media-meta-name">{items[0].file.name}</div>
                    <div className="media-meta-size">{fileSize(items[0].file.size)}{` · ${items[0].duration || "Loading duration..."}`}</div>
                  </div>
                  <div className="media-meta-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current.click()}>Replace</button>
                    <button className="btn btn-danger btn-sm" onClick={() => removeItem(items[0].id)}>Remove</button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="media-preview-wrap">
                  <img src={items[0].previewUrl} className="media-preview" alt="Preview" />
                </div>
                <div className="thumb-strip">
                  {items.map((it, idx) => (
                    <div
                      key={it.id}
                      className="thumb-item"
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", String(it.id))}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); moveItem(Number(e.dataTransfer.getData("text/plain")), it.id); }}
                    >
                      <span className="thumb-pos">{idx + 1}</span>
                      <img src={it.previewUrl} alt={`Item ${idx + 1}`} />
                      <div className="thumb-actions">
                        <button onClick={() => openCrop(it)}>Crop</button>
                        <button onClick={() => removeItem(it.id)}>Remove</button>
                      </div>
                    </div>
                  ))}
                  {items.length < 10 && (
                    <div className="thumb-add" onClick={() => fileInputRef.current.click()}>+</div>
                  )}
                </div>
                {items.length >= 2 && (
                  <div className="field-disabled-note">
                    This will publish as a {items.length}-image carousel, in the order shown above (drag thumbnails to reorder).
                    Meta crops every carousel image to match the first image's aspect ratio.
                  </div>
                )}
              </>
            )}
            <input
              type="file" ref={fileInputRef} accept={accept} multiple={!isReel} className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            />
          </div>
        </div>
      )}

      {/* ---- Step 2: Caption ---- */}
      {step === 2 && (
        <div className="create-layout">
          <div className="create-details-col" style={{ gridColumn: "1 / -1" }}>
            <div className="form-group">
              <label>Caption</label>
              <textarea
                className="form-control" maxLength={2200} rows={8} placeholder="Write a caption... use #hashtags and @mentions freely"
                value={caption} onChange={(e) => setCaption(e.target.value)}
              />
              <div className={`char-counter ${charCount > 2000 ? "over" : ""}`}>Characters: {charCount} / 2200</div>
              {(hashtags.length > 0 || mentions.length > 0) && (
                <div className="tag-hint">
                  {hashtags.map((h) => <span key={h} className="pill">{h}</span>)}
                  {mentions.map((m) => <span key={m} className="pill">{m}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Step 3: Settings ---- */}
      {step === 3 && (
        <div className="create-layout">
          <div className="create-details-col" style={{ gridColumn: "1 / -1" }}>
            <div className="form-group">
              <label>Instagram Account</label>
              <select className="form-control" value={account} onChange={(e) => setAccount(e.target.value)} disabled={accounts.length === 0}>
                {accounts.length === 0 && <option value="">No accounts connected</option>}
                {accounts.map((a) => <option key={a.id} value={a.id}>@{a.username}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Collaborators <span className="optional">(optional, up to 3)</span></label>
              <div className="chip-input">
                {collaborators.map((c) => (
                  <span key={c} className="chip">@{c} <button onClick={() => setCollaborators((prev) => prev.filter((x) => x !== c))}>×</button></span>
                ))}
                {collaborators.length < 3 && (
                  <input
                    placeholder="username" value={collabDraft}
                    onChange={(e) => setCollabDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addCollaborator(); } }}
                    onBlur={addCollaborator}
                  />
                )}
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={isAiGenerated} onChange={(e) => setIsAiGenerated(e.target.checked)} />
                This content is AI-generated or AI-altered
              </label>
              <div className="field-disabled-note">Applies Instagram's AI info disclosure label on the published post.</div>
            </div>

            <div className="form-group">
              <label>Tag People <span className="optional">(optional, single image/video only)</span></label>
              {isCarousel ? (
                <div className="field-disabled-note">Meta's API does not support tagging people on carousel images or videos.</div>
              ) : (
                <div className="chip-input">
                  {tagPeople.map((c) => (
                    <span key={c} className="chip">@{c} <button onClick={() => setTagPeople((prev) => prev.filter((x) => x !== c))}>×</button></span>
                  ))}
                  <input
                    placeholder="username" value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTagPerson(); } }}
                    onBlur={addTagPerson}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Location <span className="optional">(optional{isReel ? "" : ", Reels only"})</span></label>
              {!isReel ? (
                <div className="field-disabled-note">Meta's Content Publishing API does not support location tagging for image posts or carousels -- only for Reels.</div>
              ) : selectedLocation ? (
                <div className="location-selected">
                  <div>
                    <strong>{selectedLocation.name}</strong>
                    {locationLabel(selectedLocation) && <div className="location-sub">{locationLabel(selectedLocation)}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSelectedLocation(null); setLocationQuery(""); }}>Change</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => { setSelectedLocation(null); setLocationQuery(""); }}>Remove</button>
                  </div>
                </div>
              ) : (
                <div className="location-search">
                  <input
                    type="text" className="form-control" placeholder="Search for a location"
                    value={locationQuery} onChange={(e) => setLocationQuery(e.target.value)}
                  />
                  {locationSearching && <div className="field-disabled-note">Searching...</div>}
                  {locationError && <div className="field-disabled-note">{locationError}</div>}
                  {!locationSearching && !locationError && locationQuery.trim() && locationResults.length === 0 && (
                    <div className="field-disabled-note">No matching places found. Try the place's exact name, e.g. "Brookefields Mall".</div>
                  )}
                  {locationResults.length > 0 && (
                    <div className="location-results">
                      {locationResults.map((loc) => (
                        <div key={loc.id} className="location-result" onClick={() => { setSelectedLocation(loc); setLocationResults([]); }}>
                          <strong>{loc.name}</strong>
                          {locationLabel(loc) && <div className="location-sub">{locationLabel(loc)}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="field-disabled-note">Real results from Meta's own Pages Search -- only places Meta itself returns can be selected; free-typed text is never treated as a location.</div>
                </div>
              )}
            </div>

            {isReel && (
              <div className="form-group">
                <label>Audio / Music <span className="optional">(optional)</span></label>
                {selectedAudio ? (
                  <div className="location-selected">
                    <div>
                      <strong>{selectedAudio.title}</strong>
                      {selectedAudio.artist && <div className="location-sub">{selectedAudio.artist}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSelectedAudio(null); setAudioQuery(""); }}>Change</button>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => { setSelectedAudio(null); setAudioQuery(""); }}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className="location-search">
                    <input
                      type="text" className="form-control" placeholder="Search Meta's Sound Collection"
                      value={audioQuery} onChange={(e) => setAudioQuery(e.target.value)}
                    />
                    {audioSearching && <div className="field-disabled-note">Searching...</div>}
                    {audioError && <div className="field-disabled-note">{audioError}</div>}
                    {!audioSearching && !audioError && audioResults.length === 0 && (
                      <div className="field-disabled-note">No tracks found. Leaving this unset means the audio already embedded in your video file plays as-is.</div>
                    )}
                    {audioResults.length > 0 && (
                      <div className="location-results">
                        {audioResults.map((a) => (
                          <div key={a.id} className="location-result" onClick={() => { setSelectedAudio(a); setAudioResults([]); }}>
                            <strong>{a.title}</strong>
                            {a.artist && <div className="location-sub">{a.artist}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="field-disabled-note">Real tracks from Meta's own Instagram Audio API, Reels only -- not available for photo posts or carousels.</div>
                  </div>
                )}
              </div>
            )}

            <div className="section-title" style={{ marginTop: 8 }}>{editId ? "Schedule" : "Publishing"}</div>
            {!editId && (
              <div className="publish-toggle">
                <button type="button" className={publishNow ? "active" : ""} onClick={() => setPublishNow(true)}>Publish Now</button>
                <button type="button" className={!publishNow ? "active" : ""} onClick={() => setPublishNow(false)}>Schedule</button>
              </div>
            )}
            <div className={`schedule-fields ${!publishNow ? "open" : ""}`}>
              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <DateInput aria-label="Date" className="form-control" min={today} value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Time</label>
                  <input type="time" className="form-control" value={time} onChange={(e) => setTime(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select className="form-control" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Asia/Dubai">Asia/Dubai</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Step 4: Review ---- */}
      {step === 4 && (
        <div className="create-layout">
          <div className="review-card">
            <div className="review-account">
              {selectedAccount?.profile_picture_url && <img src={selectedAccount.profile_picture_url} alt="" />}
              <strong>@{selectedAccount?.username || "—"}</strong>
            </div>
            <div className="review-media">
              {items[0]?.isVideo ? (
                <video src={items[0].previewUrl} controls />
              ) : (
                <img src={items[0]?.previewUrl} alt="" />
              )}
              {items.length > 1 && (
                <div className="review-carousel-nav">
                  {items.map((it, i) => <span key={it.id} className={i === 0 ? "active" : ""} />)}
                </div>
              )}
            </div>
            <div className="review-caption">{caption || <em>No caption</em>}</div>
            <div className="review-meta">
              {isCarousel && <span>{items.length}-image carousel</span>}
              {collaborators.length > 0 && <span>Collaborators: {collaborators.map((c) => `@${c}`).join(", ")}</span>}
              {isAiGenerated && <span>Labeled as AI-generated content</span>}
              {!isCarousel && tagPeople.length > 0 && <span>Tagged: {tagPeople.map((c) => `@${c}`).join(", ")}</span>}
              {isReel && selectedLocation && <span>Location: {selectedLocation.name}{locationLabel(selectedLocation) ? ` (${locationLabel(selectedLocation)})` : ""}</span>}
              {isReel && selectedAudio && <span>Audio: {selectedAudio.title}{selectedAudio.artist ? ` — ${selectedAudio.artist}` : ""}</span>}
              <span>{publishNow ? "Publishing immediately" : `Scheduled for ${date} ${time} (${timezone})`}</span>
            </div>
          </div>
          <div className="create-details-col">
            <p style={{ fontSize: 13, color: "var(--gray-600)" }}>
              This preview reflects the actual media and caption that will be sent to Meta. Only the settings listed above are actually applied on publish -- anything left blank is not sent.
            </p>
          </div>
        </div>
      )}

      {submitError && <div className="login-error" style={{ margin: "16px 0" }}>{submitError}</div>}

      <div className="composer-nav">
        <button className="btn btn-ghost" onClick={goBack} disabled={step === 1 || submitting}>Back</button>
        {step < 4 ? (
          <button className="btn btn-primary" onClick={goNext} disabled={accounts.length === 0}>Next</button>
        ) : (
          <button className="btn btn-primary" onClick={submit} disabled={submitting || accounts.length === 0}>
            {submitting ? "Saving..." : editId ? "Save Changes" : publishNow ? `Publish ${isReel ? "Reel" : "Post"} Now` : `Schedule ${isReel ? "Reel" : "Post"}`}
          </button>
        )}
      </div>

      {cropTarget && (
        <div className="modal-overlay" onClick={() => setCropTarget(null)}>
          <div className="modal-box modal-lg" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setCropTarget(null)}>
              <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <h3>Edit Crop</h3>
            <div className="crop-dialog">
              <div className="crop-aspects">
                {ASPECTS.map((a) => (
                  <button key={a.key} className={cropAspectKey === a.key ? "active" : ""} onClick={() => setCropAspectKey(a.key)}>
                    {a.label}
                  </button>
                ))}
              </div>
              <div className="crop-stage">
                <Cropper
                  image={cropTarget.previewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={ASPECTS.find((a) => a.key === cropAspectKey)?.value || undefined}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setCropTarget(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={applyCrop}>Apply Crop</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
